import { useState, useEffect } from 'react';
import { TopBar } from '../../components/feature/TopBar';
import { Sidebar } from '../../components/feature/Sidebar';

export default function InverterPage() {
  const [inverterData, setInverterData] = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState('pl_001');
  const [selectedInverter, setSelectedInverter] = useState('inv_001');
  const [alertsVisible, setAlertsVisible] = useState(true);

  const plants = [
    { id: 'pl_001', name: 'Planta Solar Norte' },
    { id: 'pl_002', name: 'Planta Solar Sur' },
    { id: 'pl_003', name: 'Planta Solar Este' }
  ];

  const inverters = [
    { id: 'inv_001', name: 'Fronius Symo 10.0-3-M', plantId: 'pl_001' },
    { id: 'inv_002', name: 'Fronius Primo 5.0-1', plantId: 'pl_001' },
    { id: 'inv_003', name: 'Fronius Symo 8.2-3-M', plantId: 'pl_002' },
    { id: 'inv_004', name: 'Fronius Galvo 3.0-1', plantId: 'pl_002' },
    { id: 'inv_005', name: 'Fronius Eco 27.0-3-S', plantId: 'pl_003' }
  ];

  const filteredInverters = inverters.filter(inv => inv.plantId === selectedPlant);

  // Reset selected inverter when plant changes
  useEffect(() => {
    if (filteredInverters.length > 0) {
      setSelectedInverter(filteredInverters[0].id);
    }
  }, [selectedPlant]);

  // Simular datos de la API Fronius Inverter
  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      setTimeout(() => {
        const mockData = {
          DeviceStatus: {
            StatusCode: Math.random() > 0.9 ? 8 : 7, // 90% normal, 10% error
            ErrorCode: Math.random() > 0.9 ? 302 : 0
          },
          PAC: Math.floor(Math.random() * 2500) + 500,
          UAC: 230 + Math.floor(Math.random() * 20) - 10,
          IAC: Math.floor(Math.random() * 15) + 5,
          PDC: Math.floor(Math.random() * 2800) + 600,
          UDC: 380 + Math.floor(Math.random() * 40) - 20,
          IDC: Math.floor(Math.random() * 8) + 2,
          Temperature: 35 + Math.floor(Math.random() * 25),
          Frequency: 50 + Math.random() * 0.2 - 0.1,
          PowerFactor: 0.95 + Math.random() * 0.1 - 0.05,
          EnergyTotal: 125000 + Math.floor(Math.random() * 1000),
          EnergyDay: 15 + Math.floor(Math.random() * 10)
        };
        setInverterData(mockData);
        setLastUpdate(new Date());
        setIsLoading(false);
      }, 1000);
    };

    fetchData();
  }, [selectedInverter]);

  const getStatusInfo = (statusCode?: number) => {
    if (statusCode === undefined) return { text: 'Desconocido', color: 'gray', icon: 'ri-question-line' };
    if (statusCode === 7) return { text: 'Normal', color: 'green', icon: 'ri-checkbox-circle-line' };
    if (statusCode >= 8) return { text: 'Error', color: 'red', icon: 'ri-error-warning-line' };
    return { text: 'Advertencia', color: 'yellow', icon: 'ri-alert-line' };
  };

  const getTemperatureStatus = (temp?: number) => {
    if (!temp) return 'normal';
    if (temp > 70) return 'critical';
    if (temp > 60) return 'warning';
    return 'normal';
  };

  const getEfficiency = () => {
    if (!inverterData?.PAC || !inverterData?.PDC || inverterData.PDC === 0) return 0;
    return ((inverterData.PAC / inverterData.PDC) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64">
          <TopBar title="Estado del Inversor" lastUpdate={lastUpdate} />
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando datos del inversor...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(inverterData?.DeviceStatus?.StatusCode);
  const tempStatus = getTemperatureStatus(inverterData?.Temperature);
  const efficiency = getEfficiency();

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Estado del Inversor" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1624397640148-949b1732bb0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Monitor de Inversores</h1>
                <p className="text-blue-100 max-w-xl">
                  Supervisión detallada del rendimiento y estado de tus equipos de conversión.
                </p>
              </div>
              <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-md p-2 rounded-xl border border-white/20">
                <div className="flex flex-col gap-1">
                  <select
                    value={selectedPlant}
                    onChange={(e) => setSelectedPlant(e.target.value)}
                    className="bg-transparent border-none text-white text-sm font-bold focus:ring-0 cursor-pointer py-1"
                  >
                    {plants.map(plant => (
                      <option key={plant.id} value={plant.id} className="text-gray-900">{plant.name}</option>
                    ))}
                  </select>
                  <div className="h-px w-full bg-white/20"></div>
                  <select
                    value={selectedInverter}
                    onChange={(e) => setSelectedInverter(e.target.value)}
                    className="bg-transparent border-none text-blue-100 text-xs focus:ring-0 cursor-pointer py-1"
                  >
                    {filteredInverters.map(inv => (
                      <option key={inv.id} value={inv.id} className="text-gray-900">{inv.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Estado General */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Estado del Equipo</h3>
                <button
                  onClick={() => setAlertsVisible(!alertsVisible)}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <i className={`${alertsVisible ? 'ri-eye-line' : 'ri-eye-off-line'} text-xl`}></i>
                </button>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-${statusInfo.color}-50 text-${statusInfo.color}-600`}>
                  <i className={`${statusInfo.icon} text-3xl`}></i>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Estado Actual</p>
                  <p className={`text-xl font-bold text-${statusInfo.color}-600`}>{statusInfo.text}</p>
                </div>
              </div>
              {inverterData?.DeviceStatus?.ErrorCode > 0 && (
                <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm font-medium flex items-center">
                  <i className="ri-error-warning-fill mr-2"></i>
                  Error: {inverterData.DeviceStatus.ErrorCode}
                </div>
              )}
            </div>

            {/* Eficiencia */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Eficiencia de Conversión</h3>
              <div className="flex items-center gap-6">
                <div className="relative w-20 h-20 flex-shrink-0">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#f3f4f6"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#8b5cf6"
                      strokeWidth="3"
                      strokeDasharray={`${efficiency}, 100`}
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="ri-flashlight-fill text-purple-500 text-xl"></i>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{efficiency.toFixed(1)}%</p>
                  <p className="text-sm text-gray-500">Rendimiento DC/AC</p>
                </div>
              </div>
            </div>

            {/* Temperatura */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Temperatura Interna</h3>
              <div className="flex items-center gap-6">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${tempStatus === 'critical' ? 'bg-red-50 text-red-600' :
                  tempStatus === 'warning' ? 'bg-yellow-50 text-yellow-600' :
                    'bg-green-50 text-green-600'
                  }`}>
                  <i className="ri-temp-hot-line text-3xl"></i>
                </div>
                <div>
                  <p className={`text-3xl font-bold ${tempStatus === 'critical' ? 'text-red-600' :
                    tempStatus === 'warning' ? 'text-yellow-600' :
                      'text-gray-900'
                    }`}>
                    {inverterData?.Temperature?.toFixed(1)}°C
                  </p>
                  <p className="text-sm text-gray-500">Estado: {
                    tempStatus === 'critical' ? 'Crítico' :
                      tempStatus === 'warning' ? 'Advertencia' :
                        'Normal'
                  }</p>
                </div>
              </div>
            </div>
          </div>

          {/* Alertas */}
          {alertsVisible && (inverterData?.DeviceStatus?.StatusCode >= 8 || tempStatus !== 'normal') && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-4 animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="bg-red-100 p-2 rounded-lg">
                  <i className="ri-alarm-warning-line text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-red-900 font-bold">Atención Requerida</h4>
                  <ul className="text-red-700 text-sm mt-1 space-y-1">
                    {inverterData?.DeviceStatus?.StatusCode >= 8 && (
                      <li>• Fallo en el inversor detectado (Código {inverterData.DeviceStatus.ErrorCode})</li>
                    )}
                    {tempStatus === 'critical' && (
                      <li>• Temperatura crítica alcanzada ({inverterData?.Temperature}°C)</li>
                    )}
                    {tempStatus === 'warning' && (
                      <li>• Temperatura por encima del rango normal ({inverterData?.Temperature}°C)</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Métricas Principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-sm p-6 border border-purple-200 transition-transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white w-12 h-12 rounded-xl flex items-center justify-center shadow-sm">
                  <i className="ri-flashlight-line text-purple-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-purple-700 bg-white/50 px-2.5 py-1 rounded-full backdrop-blur-sm">SALIDA AC</span>
              </div>
              <h3 className="text-sm font-medium text-purple-900/60 mb-1">Potencia Activa</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-purple-900">{inverterData?.PAC?.toLocaleString('es-ES') || 'N/A'}</p>
                <p className="text-sm font-medium text-purple-700">W</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-sm p-6 border border-blue-200 transition-transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white w-12 h-12 rounded-xl flex items-center justify-center shadow-sm">
                  <i className="ri-battery-charge-line text-blue-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-blue-700 bg-white/50 px-2.5 py-1 rounded-full backdrop-blur-sm">ENTRADA DC</span>
              </div>
              <h3 className="text-sm font-medium text-blue-900/60 mb-1">Potencia Solar</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-blue-900">{inverterData?.PDC?.toLocaleString('es-ES') || 'N/A'}</p>
                <p className="text-sm font-medium text-blue-700">W</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-sm p-6 border border-orange-200 transition-transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white w-12 h-12 rounded-xl flex items-center justify-center shadow-sm">
                  <i className="ri-history-line text-orange-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-orange-700 bg-white/50 px-2.5 py-1 rounded-full backdrop-blur-sm">HOY</span>
              </div>
              <h3 className="text-sm font-medium text-orange-900/60 mb-1">Energía Diaria</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-orange-900">{inverterData?.EnergyDay || 'N/A'}</p>
                <p className="text-sm font-medium text-orange-700">kWh</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl shadow-sm p-6 border border-teal-200 transition-transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-white w-12 h-12 rounded-xl flex items-center justify-center shadow-sm">
                  <i className="ri-database-2-line text-teal-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-teal-700 bg-white/50 px-2.5 py-1 rounded-full backdrop-blur-sm">TOTAL</span>
              </div>
              <h3 className="text-sm font-medium text-teal-900/60 mb-1">Energía Acumulada</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-teal-900">{inverterData?.EnergyTotal?.toLocaleString('es-ES') || 'N/A'}</p>
                <p className="text-sm font-medium text-teal-700">kWh</p>
              </div>
            </div>
          </div>

          {/* Datos Eléctricos Detallados */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-plug-line mr-2 text-blue-500"></i>
                Parámetros de Salida (AC)
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Voltaje</p>
                    <p className="text-xl font-bold text-gray-900">{inverterData?.UAC || 'N/A'}</p>
                    <p className="text-xs text-gray-400">V</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Corriente</p>
                    <p className="text-xl font-bold text-gray-900">{inverterData?.IAC || 'N/A'}</p>
                    <p className="text-xs text-gray-400">A</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Frecuencia</p>
                    <p className="text-xl font-bold text-gray-900">{inverterData?.Frequency?.toFixed(2) || 'N/A'}</p>
                    <p className="text-xs text-gray-400">Hz</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <span className="text-sm font-medium text-blue-700">Factor de Potencia</span>
                  <span className="text-lg font-bold text-blue-900">{inverterData?.PowerFactor?.toFixed(3) || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-sun-line mr-2 text-orange-500"></i>
                Parámetros de Entrada (DC)
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Voltaje</p>
                    <p className="text-xl font-bold text-gray-900">{inverterData?.UDC || 'N/A'}</p>
                    <p className="text-xs text-gray-400">V</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Corriente</p>
                    <p className="text-xl font-bold text-gray-900">{inverterData?.IDC || 'N/A'}</p>
                    <p className="text-xs text-gray-400">A</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <p className="text-xs font-medium text-orange-600 mb-1">Energía Hoy</p>
                    <p className="text-lg font-bold text-orange-900">{inverterData?.EnergyDay || 'N/A'} kWh</p>
                  </div>
                  <div className="p-4 bg-teal-50 rounded-xl border border-teal-100">
                    <p className="text-xs font-medium text-teal-600 mb-1">Energía Total</p>
                    <p className="text-lg font-bold text-teal-900">{inverterData?.EnergyTotal?.toLocaleString('es-ES') || 'N/A'} kWh</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}